// ==========================================
// Chat log (3a)
// ==========================================
// Bubbles at 12px/1.45, max-width 82%, 4px tail corner. Hosts the inline workflow
// card, the TTS chip and the ephemeral +XP reward chip, plus the milestone banner
// that collapses to a one-line system message after 4s.
// ==========================================

import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

import { BuddyMessage, RewardChip, WorkflowRun } from '../../models/buddy.types';

@Component({
  selector: 'app-buddy-chat-log',
  standalone: false,
  templateUrl: './buddy-chat-log.component.html',
  styleUrls: ['./buddy-chat-log.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuddyChatLogComponent implements AfterViewChecked {
  @Input() messages: readonly BuddyMessage[] = [];
  @Input() runs: Record<string, WorkflowRun> = {};
  /** The message BB is currently reading aloud, if any. */
  @Input() speakingMessageId: string | null = null;
  @Input() ttsRate = 1;

  @Output() cancelRun = new EventEmitter<string>();
  @Output() retryRun = new EventEmitter<string>();
  @Output() decideRun = new EventEmitter<{ runId: string; approved: boolean }>();
  @Output() stopTts = new EventEmitter<void>();
  @Output() suggestionPicked = new EventEmitter<string>();

  @ViewChild('scroller') scroller?: ElementRef<HTMLElement>;

  /** Set once the log is pinned to the bottom, so streaming keeps the tail in view. */
  private stickToBottom = true;

  runFor(message: BuddyMessage): WorkflowRun | null {
    return message.runId ? this.runs[message.runId] ?? null : null;
  }

  rewardLabel(reward: RewardChip): string {
    return reward.bonus ? `+${reward.xp} XP · ${reward.multiplier}× drop` : `+${reward.xp} XP`;
  }

  onScroll(): void {
    const el = this.scroller?.nativeElement;
    if (!el) {
      return;
    }
    // 24px of slack so a near-bottom position still counts as pinned.
    this.stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  }

  ngAfterViewChecked(): void {
    if (!this.stickToBottom) {
      return;
    }
    const el = this.scroller?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }

  /** Exposed so the habitat sheet can restore the exact scroll position on back (3d). */
  get scrollTop(): number {
    return this.scroller?.nativeElement.scrollTop ?? 0;
  }

  restoreScroll(top: number): void {
    const el = this.scroller?.nativeElement;
    if (el) {
      el.scrollTop = top;
      this.stickToBottom = el.scrollHeight - top - el.clientHeight < 24;
    }
  }

  trackMessage(_: number, message: BuddyMessage): string {
    return message.id;
  }
}
